import { create } from 'zustand'
import { DEFAULT_BLOCK_PROPS, makeColumnDefs } from './blockDefaults'
import { recordHistory, undoState, redoState, emptyHistory } from './emailHistory'
import { withDerivedTracking, deriveOnFieldChange, clearTrackingIds } from './trackingTags'

let _counter = 0
const genId = () => `blk_${++_counter}_${Math.random().toString(36).slice(2, 7)}`

/**
 * Page-level settings. Exported so loading a record can REPLACE the whole object
 * rather than merge onto whatever the previous template left behind — see
 * setEmailSettings below.
 */
export const DEFAULT_EMAIL_SETTINGS = {
  backgroundColor: '#F7F9FC',
  backgroundType: 'solid',
  contentBackground: '#ffffff',
  contentBackgroundType: 'solid',
  contentWidth: '600',
  fontFamily: 'Arial, Helvetica, sans-serif',
  linkColor: '#2563eb',
  preheaderText: '',
}

function deepClone(obj) {
  return JSON.parse(JSON.stringify(obj))
}

function makeBlock(blockType, propsOverride = {}, existingBlocks = []) {
  const base = DEFAULT_BLOCK_PROPS[blockType]
  if (!base) return null
  let props = { ...deepClone(base), ...propsOverride }
  // Columns: generate columnDefs if not provided
  if (blockType === 'columns' && (!props.columnDefs || props.columnDefs.length === 0)) {
    props.columnDefs = makeColumnDefs(Number(props.columns) || 2)
  } else if (blockType === 'columns' && props.columnDefs?.length) {
    // Inserting a prefilled columns block (e.g. a multi-element universal
    // component) — give every column + nested block a fresh id so multiple
    // copies never collide with each other or the saved original.
    props.columnDefs = props.columnDefs.map(col => ({
      ...col,
      id: `col_${genId()}`,
      blocks: (col.blocks || []).map(sb => ({ ...deepClone(sb), id: genId() })),
    }))
  }
  // Click-tracking ids: derived once, here, from whatever label/url the block
  // starts with — never left empty for the user to forget about. See
  // trackingTags.js for which block types this applies to and why.
  props = withDerivedTracking(blockType, props, existingBlocks)
  return { id: genId(), type: blockType, props }
}

// Traverse block tree and apply fn — returns new blocks array
function mapBlockTree(blocks, fn) {
  return blocks.map(b => {
    const updated = fn(b)
    if (updated.type === 'columns' && updated.props.columnDefs) {
      return {
        ...updated,
        props: {
          ...updated.props,
          columnDefs: updated.props.columnDefs.map(col => ({
            ...col,
            blocks: mapBlockTree(col.blocks || [], fn),
          })),
        },
      }
    }
    return updated
  })
}

// Assign fresh ids to a template's block tree (top-level + column defs + nested).
/**
 * Header-bar links must be `{ id, kind, label, url }` objects with a UNIQUE id —
 * PropertiesPanel edits and deletes them by matching `l.id === id`.
 *
 * Several pre-built templates (insurance, fintech, fmcg) declare them as plain
 * strings instead: `links: ['Claims', 'Support']`. Every entry then has
 * `id === undefined`, so `l.id === id` matched EVERY link — editing one renamed
 * them all (ET_034) and deleting one removed them all (ET_035).
 *
 * Normalising here fixes every current and future template regardless of shape,
 * rather than relying on 18 hand-edited literals staying correct.
 */
function normalizeHeaderLinks(props) {
  if (!props || props.links == null) return props
  // healthcare.js / logistics.js pass a COMMA-SEPARATED STRING
  // (`links: 'Home,My Care,Contact'`). Both render paths do `(p.links || []).map()`
  // and a string has no .map, so those categories threw and rendered a blank
  // screen (ET_037). Split it into the proper shape.
  if (typeof props.links === 'string') {
    props.links = props.links.split(',').map(s => s.trim()).filter(Boolean)
  }
  if (!Array.isArray(props.links)) { props.links = []; return props }
  const seen = new Set()
  props.links = props.links.map(l => {
    const base = typeof l === 'string'
      ? { kind: 'link', label: l, url: '#', src: '', alt: '' }
      : { kind: 'link', url: '#', src: '', alt: '', ...l }
    // Regenerate on missing OR duplicate ids — a duplicate is just as ambiguous.
    let id = base.id
    if (!id || seen.has(id)) id = `hl_${genId()}`
    seen.add(id)
    return { ...base, id }
  })
  return props
}

/** Same normalisation applied to already-id'd blocks loaded from storage. */
function normalizeBlocksLinks(blocks) {
  if (!Array.isArray(blocks)) return blocks || []
  return blocks.map(b => {
    const nb = { ...b, props: normalizeHeaderLinks({ ...(b.props || {}) }) }
    if (nb.type === 'columns' && Array.isArray(nb.props.columnDefs)) {
      nb.props.columnDefs = nb.props.columnDefs.map(col => ({
        ...col,
        blocks: normalizeBlocksLinks(col.blocks || []),
      }))
    }
    return nb
  })
}

function assignTemplateIds(blocks) {
  // Defensive: a non-array here (a bare block object from a malformed template)
  // used to throw on .map and blank the whole editor rather than degrade.
  if (!Array.isArray(blocks)) blocks = blocks ? [blocks] : []
  return blocks.map(b => {
    const nb = { id: genId(), type: b.type, props: normalizeHeaderLinks(deepClone(b.props || {})) }
    if (nb.type === 'columns' && Array.isArray(nb.props.columnDefs)) {
      nb.props.columnDefs = nb.props.columnDefs.map(col => ({
        ...col,
        id: `col_${genId()}`,
        blocks: assignTemplateIds(col.blocks || []),
      }))
    }
    return nb
  })
}

// Find a block anywhere in the tree (returns { block, colDefId, parentBlockId } or null)
function findBlockInTree(blocks, blockId) {
  for (const b of blocks) {
    if (b.id === blockId) return { block: b, colDefId: null, parentBlockId: null }
    if (b.type === 'columns' && b.props.columnDefs) {
      for (const col of b.props.columnDefs) {
        for (const sub of col.blocks || []) {
          if (sub.id === blockId) return { block: sub, colDefId: col.id, parentBlockId: b.id }
        }
      }
    }
  }
  return null
}

// History fields to merge into a set() BEFORE a mutation. `tag` coalesces
// continuous edits (slider drag, typing) into a single undo step.
const hist = (state, tag) => recordHistory(state, tag, Date.now())

const useEmailDesignerStore = create((set, get) => ({
  blocks: [],
  selectedBlockId: null,
  // Context for sub-blocks: null for top-level, { colDefId, parentBlockId } for column children
  selectedBlockContext: null,
  // Which text/heading block is being edited inline on the canvas (toolbar on the widget).
  editingTextId: null,
  setEditingText: (id) => set({ editingTextId: id }),
  // Bumped after a block is saved as universal so the Universal palette reloads.
  universalRefreshToken: 0,
  bumpUniversalRefresh: () => set(s => ({ universalRefreshToken: s.universalRefreshToken + 1 })),
  emailSettings: { ...DEFAULT_EMAIL_SETTINGS },

  // ── Undo/redo history ──────────────────────────────────────────────────────
  ...emptyHistory(),
  canUndo: () => (get()._past || []).length > 0,
  canRedo: () => (get()._future || []).length > 0,
  undo: () => set(state => undoState(state) || {}),
  redo: () => set(state => redoState(state) || {}),

  // ── Top-level block operations ─────────────────────────────────────────────

  addBlock: (blockType, afterIndex = -1, propsOverride = {}) => {
    let newBlockId = null
    set(state => {
      const newBlock = makeBlock(blockType, propsOverride, state.blocks)
      if (!newBlock) return {}
      newBlockId = newBlock.id
      const arr = [...state.blocks]
      arr.splice(afterIndex >= 0 ? afterIndex + 1 : arr.length, 0, newBlock)
      return { ...hist(state), blocks: arr, selectedBlockId: newBlock.id, selectedBlockContext: null }
    })
    return newBlockId
  },

  removeBlock: (blockId) => set(state => {
    // Remove from top-level first
    const topFiltered = state.blocks.filter(b => b.id !== blockId)
    if (topFiltered.length !== state.blocks.length) {
      return {
        ...hist(state),
        blocks: topFiltered,
        selectedBlockId: state.selectedBlockId === blockId ? null : state.selectedBlockId,
        selectedBlockContext: null,
      }
    }
    // Otherwise traverse tree (sub-block removal)
    const blocks = state.blocks.map(b => {
      if (b.type !== 'columns' || !b.props.columnDefs) return b
      return {
        ...b,
        props: {
          ...b.props,
          columnDefs: b.props.columnDefs.map(col => ({
            ...col,
            blocks: col.blocks.filter(sb => sb.id !== blockId),
          })),
        },
      }
    })
    return {
      ...hist(state),
      blocks,
      selectedBlockId: state.selectedBlockId === blockId ? null : state.selectedBlockId,
      selectedBlockContext: state.selectedBlockId === blockId ? null : state.selectedBlockContext,
    }
  }),

  updateBlock: (blockId, updates) => set(state => ({
    ...hist(state, `update:${blockId}`),
    blocks: mapBlockTree(state.blocks, b => {
      if (b.id !== blockId) return b
      const merged = { ...b.props, ...updates }
      // A few fields (Image/Countdown link, the header logo link) start empty
      // on a fresh block and only get filled in here, well after creation —
      // this is their one chance to pick up a tracking id (see trackingTags.js).
      return { ...b, props: deriveOnFieldChange(b.type, merged, state.blocks) }
    }),
  })),

  duplicateBlock: (blockId) => {
    const { blocks } = get()
    // Fresh tracking id(s) on the copy — a duplicated button is a distinct
    // instance, and sharing the original's id would silently merge their click
    // counts. `existingBlocks` includes the ORIGINAL being copied, so the new
    // id is guaranteed to differ from it too, not just from everything else.
    const recloned = (block, existingBlocks) => ({
      ...deepClone(block),
      id: genId(),
      props: withDerivedTracking(block.type, clearTrackingIds(deepClone(block.props)), existingBlocks),
    })
    // Try top-level first
    const topIdx = blocks.findIndex(b => b.id === blockId)
    if (topIdx !== -1) {
      set(s => {
        const clone = recloned(s.blocks[topIdx], s.blocks)
        const arr = [...s.blocks]
        arr.splice(topIdx + 1, 0, clone)
        return { ...hist(s), blocks: arr, selectedBlockId: clone.id, selectedBlockContext: null }
      })
      return
    }
    // Sub-block duplication
    set(state => ({
      ...hist(state),
      blocks: state.blocks.map(b => {
        if (b.type !== 'columns' || !b.props.columnDefs) return b
        return {
          ...b,
          props: {
            ...b.props,
            columnDefs: b.props.columnDefs.map(col => {
              const idx = col.blocks.findIndex(sb => sb.id === blockId)
              if (idx === -1) return col
              const clone = recloned(col.blocks[idx], state.blocks)
              const arr = [...col.blocks]
              arr.splice(idx + 1, 0, clone)
              return { ...col, blocks: arr }
            }),
          },
        }
      }),
    }))
  },

  selectBlock: (blockId, context = null) => set(state => ({
    selectedBlockId: blockId,
    selectedBlockContext: context,
    // Leave inline text-edit mode unless the same block stays selected.
    editingTextId: state.editingTextId === blockId ? state.editingTextId : null,
  })),

  reorderBlocks: (activeId, overId) => set(state => {
    const arr = [...state.blocks]
    const from = arr.findIndex(b => b.id === activeId)
    const to = arr.findIndex(b => b.id === overId)
    if (from === -1 || to === -1 || from === to) return {}
    const [item] = arr.splice(from, 1)
    arr.splice(to, 0, item)
    return { ...hist(state), blocks: arr }
  }),

  // ── Column sub-block operations ────────────────────────────────────────────

  addSubBlock: (colDefId, parentBlockId, blockType, afterIndex = -1, propsOverride = {}) => {
    let newBlockId = null
    set(state => {
      const newBlock = makeBlock(blockType, propsOverride, state.blocks)
      if (!newBlock) return {}
      newBlockId = newBlock.id
      return {
        ...hist(state),
        blocks: state.blocks.map(b => {
          if (b.id !== parentBlockId) return b
          return {
            ...b,
            props: {
              ...b.props,
              columnDefs: b.props.columnDefs.map(col => {
                if (col.id !== colDefId) return col
                const arr = [...col.blocks]
                arr.splice(afterIndex >= 0 ? afterIndex + 1 : arr.length, 0, newBlock)
                return { ...col, blocks: arr }
              }),
            },
          }
        }),
        selectedBlockId: newBlock.id,
        selectedBlockContext: { colDefId, parentBlockId },
      }
    })
    return newBlockId
  },

  reorderSubBlocks: (colDefId, parentBlockId, activeId, overId) => set(state => {
    if (activeId === overId) return {}
    return {
      ...hist(state),
      blocks: state.blocks.map(b => {
        if (b.id !== parentBlockId) return b
        return {
          ...b,
          props: {
            ...b.props,
            columnDefs: b.props.columnDefs.map(col => {
              if (col.id !== colDefId) return col
              const arr = [...col.blocks]
              const from = arr.findIndex(sb => sb.id === activeId)
              const to = arr.findIndex(sb => sb.id === overId)
              if (from === -1 || to === -1 || from === to) return col
              const [item] = arr.splice(from, 1)
              arr.splice(to, 0, item)
              return { ...col, blocks: arr }
            }),
          },
        }
      }),
    }
  }),

  // Update a column definition (not sub-block) — e.g. background color
  updateColumnDef: (parentBlockId, colDefId, colUpdates) => set(state => ({
    ...hist(state, `coldef:${colDefId}`),
    blocks: state.blocks.map(b => {
      if (b.id !== parentBlockId) return b
      return {
        ...b,
        props: {
          ...b.props,
          columnDefs: b.props.columnDefs.map(col =>
            col.id === colDefId ? { ...col, ...colUpdates } : col
          ),
        },
      }
    }),
  })),

  // Resize columns count (add/remove columnDefs)
  setColumnsCount: (parentBlockId, count) => set(state => ({
    ...hist(state),
    blocks: state.blocks.map(b => {
      if (b.id !== parentBlockId) return b
      const current = b.props.columnDefs || []
      let columnDefs
      if (count > current.length) {
        const extra = makeColumnDefs(count - current.length)
        columnDefs = [...current, ...extra]
      } else {
        columnDefs = current.slice(0, count)
      }
      return { ...b, props: { ...b.props, columns: count, columnDefs } }
    }),
  })),

  // ── Global ─────────────────────────────────────────────────────────────────

  // Partial update — for the Properties panel, where the user edits one field.
  updateEmailSettings: (updates) => set(state => ({
    ...hist(state, 'settings'),
    emailSettings: { ...state.emailSettings, ...updates },
  })),

  /**
   * Full REPLACE, for loading a saved record. Use this instead of
   * updateEmailSettings when opening a template: a merge would keep the
   * previously-open template's values for any key the saved record omits, so a
   * template with no preheader would silently inherit the last one's.
   * Missing keys fall back to defaults, not to whatever was on screen.
   */
  setEmailSettings: (settings) => set({
    emailSettings: { ...DEFAULT_EMAIL_SETTINGS, ...(settings || {}) },
  }),

  // ── Custom variables ───────────────────────────────────────────────────────
  customVariables: [],

  addCustomVariable: (variable) => set(state => ({
    ...hist(state),
    customVariables: [...state.customVariables, variable],
  })),

  removeCustomVariable: (id) => set(state => ({
    ...hist(state),
    customVariables: state.customVariables.filter(v => v.id !== id),
  })),

  setCustomVariables: (customVariables) => set({ customVariables: customVariables || [] }),

  clearAll: () => set(state => ({
    ...hist(state),
    blocks: [], selectedBlockId: null, selectedBlockContext: null, customVariables: [],
    // Page settings must reset too, otherwise starting a new email inherits the
    // preheader text / background / font of the template that was open before.
    emailSettings: { ...DEFAULT_EMAIL_SETTINGS },
  })),

  // Load blocks (e.g. opening a saved template) — resets history, not an undo step.
  // Links are normalised on the way in as well: a template saved from one of the
  // affected pre-builts has the broken string shape baked into its stored JSON.
  setBlocks: (blocks) => set({ blocks: normalizeBlocksLinks(blocks), ...emptyHistory() }),

  // Load a pre-built template (array of { type, props } with no ids). Assigns
  // fresh ids to every node (incl. nested column blocks). `atTop`/`atBottom`
  // insert a block group (header/footer style); otherwise replace the canvas.
  loadTemplate: (templateBlocks, mode = 'replace') => set(state => {
    const fresh = assignTemplateIds(templateBlocks || [])
    if (mode === 'top') return { ...hist(state), blocks: [...fresh, ...state.blocks] }
    if (mode === 'bottom') return { ...hist(state), blocks: [...state.blocks, ...fresh] }
    return { ...hist(state), blocks: fresh, selectedBlockId: null, selectedBlockContext: null, editingTextId: null }
  }),

  // Helper exposed so components can call it
  findBlockById: (blockId) => findBlockInTree(get().blocks, blockId),

  // Returns variables used in email content + custom variable definitions
  getUsedVariables: () => {
    const { blocks, customVariables } = get()
    const VAR_REGEX = /\$[a-zA-Z_][a-zA-Z0-9_]*/g
    const found = new Set()

    function scanBlock(block) {
      const raw = JSON.stringify(block.props || '')
      const matches = raw.match(VAR_REGEX) || []
      matches.forEach(m => found.add(m.slice(1)))
      if (block.type === 'columns' && block.props?.columnDefs) {
        block.props.columnDefs.forEach(col =>
          (col.blocks || []).forEach(scanBlock)
        )
      }
    }

    blocks.forEach(scanBlock)

    return {
      variables: [...found],
      customVariables: customVariables.map(({ label, token }) => ({ label, token })),
    }
  },
}))

export default useEmailDesignerStore
